import { inject, injectable } from 'tsyringe';
import { LoggerService } from '../../../../shared/services/logger.service';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import {
  ConditionType,
  conditionType,
} from './decrators/condition-type.decorator';
import {
  kDITGameEventsListener,
  OwGameEventsWrapper,
} from '../../../ow-odk-wrappers/ow-game-events-listener-wrapper';
import { OWGamesEventsDelegate } from '../../../../odk-ts';
import { PromiseResolver } from '../../../../shared/promise-resolver';
import { IGameEventTrigger } from '../../datastore-service/models/game-event-trigger.model';
import {
  IPromotionWindowManagerService,
  kNameOfPromotionWindowManagerService,
} from '../../../interfaces/promotion-window-manager-service.interface';
import { ContentRecommendationUserAction } from '../../../../shared/constants';
import {
  kNameOfPromotionAnalyticsService,
  PromotionAnalyticsService,
} from '../../promotion-analytics/promotion-analytics.service';

const kSecondaryGameTriggerGames = [21216];

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Promotion)
export class ConditionGameInfoReceived
  implements ICondition, OWGamesEventsDelegate
{
  private readonly logger = LoggerService.getCategory(
    'ConditionGameInfoReceived',
  );

  private promiseResolver: PromiseResolver;
  private waitingForEvents = false;
  private requiredFeatures: string[];
  private triggers: IGameEventTrigger[];
  private disableTriggers: IGameEventTrigger[];
  private triggered = false;
  private infoDB: any = null;
  private gameId: number;

  // ---------------------------------------------------------------------------
  constructor(
    @inject(kDITGameEventsListener)
    private readonly gameEventListener: OwGameEventsWrapper,
    @inject(kNameOfPromotionWindowManagerService)
    private readonly promotionWinManager: IPromotionWindowManagerService,
    @inject(kNameOfPromotionAnalyticsService)
    private readonly promotionAnalyticsService: PromotionAnalyticsService,
  ) {
    this.gameEventListener.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  public shouldSkip(context: ConditionContext) {
    if (
      !context?.game?.gameEventTriggers ||
      context?.game?.gameEventTriggers.length === 0
    ) {
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    try {
      this.triggers = context?.game?.gameEventTriggers;
      this.disableTriggers = context?.game?.disableTriggers;
      this.requiredFeatures = ['phase', 'game_info', 'match_info'];
      // stopping any previous listeners
      this.gameId = context?.game?.id ?? 0;
      this.stopListening();
      await this.gameEventListener?.start(this.requiredFeatures);
      await this.obtainGameInfos();
      const handled = await this.handleInfoUpdate();
      this.waitingForEvents = true;
      if (handled) {
        return [true];
      }

      this.promiseResolver = new PromiseResolver();
      this.logger.info('Waiting for Info updates');
      await this.promiseResolver?.promise();
      return [true];
    } catch (error) {
      this.logger.error('Error matching game eventsF', error);
      return [false];
    }
  }

  // ---------------------------------------------------------------------------
  private async disableTriggersFound() {
    const foundTriggers = this.disableTriggers?.find(this.findTrigger());
    return !!foundTriggers;
  }

  // ---------------------------------------------------------------------------
  private findTrigger(): (
    value: IGameEventTrigger,
    index: number,
    obj: IGameEventTrigger[],
  ) => unknown {
    return (trigger) => {
      this.logger.info(
        `Checking trigger: ${JSON.stringify(
          trigger,
        )} against infoDB: ${JSON.stringify(this.infoDB)}`,
      );

      const infoValue = this.infoDB?.[trigger.category]?.[trigger.keyName];

      if (trigger.type !== 'info') {
        return false;
      }

      if (trigger?.values?.length === 1 && trigger.values[0] === '*') {
        return !!infoValue;
      }

      if ((trigger?.values?.length ?? 0) > 1) {
        return trigger.values.includes(infoValue);
      }

      return true;
    };
  }

  // ---------------------------------------------------------------------------
  private stopListening() {
    this.gameEventListener?.stop();
  }

  // ---------------------------------------------------------------------------
  private async secondaryGameTrigger() {
    if (!kSecondaryGameTriggerGames.includes(this.gameId)) {
      this.logger.info('No secondary trigger needed for this game');
      return true;
    }

    if (!this.infoDB) {
      return false;
    }

    if (!this.infoDB.game_info) {
      return false;
    }

    if (!this.infoDB.game_info.game_type) {
      return false;
    }

    return typeof this.infoDB.game_info.game_type !== 'undefined';
  }

  // ---------------------------------------------------------------------------
  private async obtainGameInfos() {
    try {
      const { res: info } = await this.gameEventListener?.getInfo();
      this.infoDB = info;
      this.logger.info('Obtained initial info', this.infoDB);
    } catch (error) {
      this.logger.error('Failed to obtain info', error);
    }
  }

  // ---------------------------------------------------------------------------
  private async handleInfoUpdate() {
    try {
      const foundTriggers = this.triggers?.find(this.findTrigger());
      const secondaryTrigger = await this.secondaryGameTrigger();
      const disableTrigger = await this.disableTriggersFound();
      if (foundTriggers && secondaryTrigger && !disableTrigger) {
        this.logger.info('Found Trigger Info');
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  private async setToInfoDB(info: any) {
    this.logger.info('onInfoUpdates', JSON.stringify(info));
    let tempDB = { ...this.infoDB };
    if (!info) {
      return;
    }

    // eslint-disable-next-line guard-for-in
    for (const i in info) {
      tempDB = { ...tempDB, [i]: { ...tempDB[i], ...info[i] } };
    }

    this.infoDB = tempDB;
  }

  // ---------------------------------------------------------------------------
  // OWGamesEventsDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onInfoUpdates(info: any) {
    if (!this.waitingForEvents) {
      return;
    }

    this.logger.info('Received Info update, checking triggers', info);

    this.setToInfoDB(info);
    const disableFound = await this.disableTriggersFound();
    if (disableFound) {
      this.logger.info('Disable triggers found, closing');
      this.stopListening();
      const promotionInfo = this.promotionWinManager?.getPromotionInfo();

      if (promotionInfo) {
        this.promotionAnalyticsService.reportContentRecommend(
          promotionInfo?.gameId,
          promotionInfo?.promotionId,
          promotionInfo?.promotionType,
          ContentRecommendationUserAction.IgnoredByChangedInfo,
        );
        this.promotionWinManager.close();
        return;
      }
    }

    const handled = await this.handleInfoUpdate();
    if (!handled) {
      return;
    }

    this.triggered = true;
    this.promiseResolver?.resolve();
  }

  // ---------------------------------------------------------------------------
  onNewEvents(e: any) {
    // Not implemented
  }
}
